// Folder tree for the explorer. Single-child folder chains are compressed
// ("crates/pathbase-api/src"), folders sort before files, both by name.

export interface TreeNode {
  name: string;
  path: string;
  folders: TreeNode[];
  files: string[];
}

interface Building {
  name: string;
  path: string;
  folders: Map<string, Building>;
  files: string[];
}

/** `dirs` adds folders whose files are not known yet (a lazily loaded repository tree). */
export function buildTree(paths: string[], dirs: string[] = []): TreeNode {
  const root: Building = { name: '', path: '', folders: new Map(), files: [] };
  const folderOf = (parts: string[]) => {
    let node = root;
    for (const name of parts) {
      const path = node.path ? node.path + '/' + name : name;
      let next = node.folders.get(name);
      if (!next) {
        next = { name, path, folders: new Map(), files: [] };
        node.folders.set(name, next);
      }
      node = next;
    }
    return node;
  };
  for (const d of dirs) folderOf(d.split('/'));
  for (const p of paths) {
    const parts = p.split('/');
    parts.pop();
    folderOf(parts).files.push(p);
  }
  const finish = (b: Building): TreeNode => {
    let node = b;
    let name = b.name;
    while (node !== root && !node.files.length && node.folders.size === 1) {
      node = [...node.folders.values()][0];
      name += '/' + node.name;
    }
    return {
      name,
      path: node.path,
      folders: [...node.folders.values()].sort((a, c) => a.name.localeCompare(c.name)).map(finish),
      files: [...node.files].sort((a, c) => a.localeCompare(c)),
    };
  };
  return finish(root);
}

/** Files in display order (depth-first, folders before files). */
export function treeOrder(node: TreeNode): string[] {
  return [...node.folders.flatMap(treeOrder), ...node.files];
}

export function descendants(node: TreeNode): string[] {
  return treeOrder(node);
}

export function basename(p: string): string {
  return p.slice(p.lastIndexOf('/') + 1);
}

export function dirname(p: string): string {
  const i = p.lastIndexOf('/');
  return i < 0 ? '' : p.slice(0, i);
}

export function fileIcon(path: string): [string, string] {
  const ext = path.split('.').pop()?.toLowerCase() ?? '';
  const map: Record<string, [string, string]> = {
    rs: ['Rs', 'rust'],
    ts: ['TS', 'ts'],
    tsx: ['TX', 'ts'],
    js: ['JS', 'js'],
    jsx: ['JX', 'js'],
    svelte: ['Sv', 'ts'],
    py: ['Py', 'py'],
    go: ['Go', 'go'],
    json: ['{}', 'json'],
    toml: ['Tm', 'json'],
    yml: ['Y', 'json'],
    yaml: ['Y', 'json'],
    md: ['M↓', 'markdown'],
    graphql: ['◇', 'graphql'],
    gql: ['◇', 'graphql'],
    css: ['#', 'css'],
    html: ['<>', 'html'],
    sh: ['$', 'sh'],
    sql: ['Sq', 'sql'],
    proto: ['Pb', 'graphql'],
  };
  return map[ext] ?? ['≡', 'other'];
}
